import { z } from "zod/v4";
import { ReferenceTypeV4, ReferenceChipV4 } from "@/domain/entities/BookingV4";

// Ported from create-booking-v3/components/stt-manual-reference/validationSttManualReference.ts
// Reuses the same i18n error keys (bookingV3.sttManualReference.error.*) — no new message strings.
export const noSttSchema = z.string().check(
  z.refine(
    (val) =>
      val.match(/^10LP/gi) || val.match(/^98LP/gi) || val.match(/^19LP/gi),
    {
      error: "bookingV3.sttManualReference.error.sttManual"
    }
  ),
  z.trim()
);

export const sttReferenceSchema = z
  .string()
  .nonempty("bookingV3.sttManualReference.error.required")
  .min(3, "bookingV4.referenceNumber.error.minimumRef");

export const sttReferenceOptionalSchema = z
  .string()
  .refine((val) => val === "" || val.length >= 3, {
    message: "bookingV4.referenceNumber.error.minimumRef"
  });

export type AccountTypeV4 = "pos" | "client" | "internal";
export type FormContextV4 = "retail" | "client";
export type ReferenceTypeV4Value = typeof ReferenceTypeV4[keyof typeof ReferenceTypeV4];

// PRD §3.4.2 — STT Manual prefix must match the account type placing the booking.
const STT_MANUAL_PREFIX_BY_ACCOUNT_TYPE: Record<AccountTypeV4, string> = {
  pos: "10LP",
  client: "98LP",
  internal: "98LP"
};

/**
 * Classifies a raw reference-number entry into a ReferenceTypeV4.
 * - stt_manual: prefix matches the account type's expected STT Manual prefix.
 * - shipment_id: prefix "CP" AND formContext is "client" (Retail keeps CP as external_ref).
 * - external_ref: fallback (prefix mismatch, free-form, or CP on Retail).
 */
export const classifyReference = (
  value: string,
  accountType: AccountTypeV4,
  formContext: FormContextV4,
  currentChips: ReferenceChipV4[]
): ReferenceTypeV4Value => {
  const expectedPrefix = STT_MANUAL_PREFIX_BY_ACCOUNT_TYPE[accountType];

  if (accountType !== "pos" && expectedPrefix && value.toUpperCase().startsWith(expectedPrefix)) {
    return ReferenceTypeV4.STT_MANUAL;
  }

  if(accountType === "pos") {
    const isSttManualPosExist = currentChips.find(chip => chip.value.toUpperCase()?.startsWith("19LP") || chip.value.toUpperCase()?.startsWith("10LP") )

    if(isSttManualPosExist) {
      if((
        value.toUpperCase().startsWith("10LP") && 
        isSttManualPosExist.value.toUpperCase().startsWith("19LP")
      ) || (
        value.toUpperCase().startsWith("19LP") && 
        isSttManualPosExist.value.toUpperCase().startsWith("10LP")
      )) {
        return ReferenceTypeV4.EXTERNAL_REF;
      }
    }

    if(value.toUpperCase().startsWith("10LP") || value.toUpperCase().startsWith("19LP")) {
      return ReferenceTypeV4.STT_MANUAL;
    }
  }

  if (formContext === "client" && /^CP/i.test(value)) {
    return ReferenceTypeV4.SHIPMENT_ID;
  }

  return ReferenceTypeV4.EXTERNAL_REF;
};
